import React from "react";
import { useNavigate } from "react-router-dom";
import { Lock, Inbox, Loader2, ArrowUpRight, Search } from "lucide-react";
import { toneOf } from "../lib/fmt";
import { WORLDS } from "../lib/worlds";
import { ROLE_LABEL, useApp } from "../context/AppCtx";

export const Pill = ({ children, tone, className = "", ...p }) => <span className={`pill ${tone || toneOf(children)} ${className}`} {...p}>{children}</span>;

export function Hero({ world, actions = [], compact }) {
  const W = WORLDS[world];
  return (
    <section className={`relative overflow-hidden rounded-[22px] border border-[#E8E3D9] bg-[var(--surface)] ${compact ? "min-h-[190px]" : "min-h-[250px]"} rise`} data-testid={`hero-${world}`}>
      <img src={W.image} alt="" className="absolute right-0 top-0 h-full w-[68%] object-cover" />
      <div className="absolute inset-0" style={{ background: "linear-gradient(90deg, var(--surface) 30%, color-mix(in srgb, var(--surface) 82%, transparent) 46%, transparent 78%)" }} />
      <div className="absolute inset-0 grain pointer-events-none" />
      <div className="relative z-10 p-7 lg:p-9 max-w-[640px]">
        <div className="eyebrow mb-3">{W.hero.eyebrow}</div>
        <h1 className="font-serif text-4xl sm:text-5xl lg:text-[54px] leading-[1.04] tracking-tight text-[#1C1917]">{W.hero.title}</h1>
        <p className="mt-4 text-[15px] text-[#57534E] max-w-[460px]">{W.hero.lead}</p>
        {W.client && <div className="mt-4 inline-flex items-center gap-2 pill bg-white/80 border-[#E5E0D5] text-[#57534E]"><Lock size={11} />Isolated client environment · {W.client}</div>}
      </div>
      {actions.length > 0 && (
        <div className="hidden md:block absolute z-10 right-5 top-5 rounded-2xl bg-[#111827]/80 backdrop-blur-md border border-white/15 p-3 text-white" data-testid="hero-quick-actions">
          <div className="text-[11px] font-semibold mb-2 px-1 text-white/80">Quick Actions</div>
          <div className="flex gap-1">
            {actions.map((a) => (
              <button key={a.label} data-testid={`quick-action-${a.label.toLowerCase().replace(/\s+/g, "-")}`} onClick={a.onClick} className="w-[74px] flex flex-col items-center gap-1.5 p-2 rounded-xl hover:bg-white/10 transition-colors">
                <span className="w-9 h-9 rounded-lg border border-white/20 grid place-items-center"><a.icon size={16} /></span><span className="text-[10.5px] leading-tight text-center">{a.label}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}

export function Kpi({ icon: Icon, label, value, sub, delta, testid }) {
  return (
    <div className="card p-4 hover:-translate-y-0.5 hover:shadow-md hover:border-[#C5A059] transition-[transform,box-shadow,border-color] duration-200" data-testid={testid || "kpi-card-metric"}>
      <div className="flex items-center gap-2 text-[11.5px] text-[#78716C] font-medium">{Icon && <span className="w-7 h-7 rounded-lg bg-[var(--chip)] text-[var(--accent)] grid place-items-center"><Icon size={14} /></span>}{label}</div>
      <div className="mt-2 text-[24px] font-bold tracking-tight tnum">{value}</div>
      <div className="flex items-center gap-2 text-[11px] text-[#8A847C]">{delta && <span className="text-[#2D6A4F] font-semibold">{delta}</span>}{sub}</div>
    </div>
  );
}
export const KpiStrip = ({ items }) => <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3 my-5">{items.map((k) => <Kpi key={k.label} {...k} />)}</div>;

export function Panel({ title, eyebrow, actions, children, className = "", pad = true, testid }) {
  return (
    <section className={`card ${className}`} data-testid={testid}>
      {(title || actions) && (
        <div className="flex items-center gap-3 px-5 pt-4 pb-3">
          <div className="flex-1 min-w-0">{eyebrow && <div className="eyebrow !text-[10px] mb-0.5">{eyebrow}</div>}{title && <h3 className="font-serif text-[18px] font-semibold text-[#1C1917] truncate">{title}</h3>}</div>
          {actions}
        </div>
      )}
      <div className={pad ? "px-5 pb-5" : ""}>{children}</div>
    </section>
  );
}

export const Loading = ({ label = "Loading intelligence…" }) => <div className="flex items-center gap-2 text-[13px] text-[#8A847C] p-6" data-testid="loading-state"><Loader2 size={16} className="animate-spin" />{label}</div>;
export const Empty = ({ label = "Nothing here yet.", children }) => <div className="flex flex-col items-center gap-2 text-center text-[13px] text-[#8A847C] p-8" data-testid="empty-state"><Inbox size={22} />{label}{children}</div>;

export function Restricted({ world }) {
  const { role } = useApp();
  return (
    <div className="card p-10 text-center max-w-xl mx-auto mt-16" data-testid="restricted-state">
      <div className="w-12 h-12 rounded-2xl bg-[#FBECE7] text-[#A24A2F] grid place-items-center mx-auto"><Lock size={20} /></div>
      <h2 className="font-serif text-2xl mt-4">Access restricted</h2>
      <p className="text-sm text-[#57534E] mt-2">The <b>{ROLE_LABEL[role]}</b> role is not authorised for <b>{WORLDS[world]?.label}</b>. Data segregation is enforced server-side. Switch role in the top bar to continue the demonstration.</p>
    </div>
  );
}

export function PageHead({ title, sub, actions, eyebrow }) {
  return (
    <div className="flex flex-wrap items-end gap-4 mb-5 rise">
      <div className="flex-1 min-w-[260px]">{eyebrow && <div className="eyebrow mb-1">{eyebrow}</div>}<h1 className="font-serif text-3xl lg:text-[36px] tracking-tight">{title}</h1>{sub && <p className="text-[14px] text-[#57534E] mt-1 max-w-3xl">{sub}</p>}</div>
      <div className="flex flex-wrap gap-2">{actions}</div>
    </div>
  );
}

export function DataTable({ rows, columns, onRow, searchKeys = ["name"], filters = [], testid = "data-table", selectedId, empty }) {
  const [q, setQ] = React.useState("");
  const [f, setF] = React.useState({});
  const [sort, setSort] = React.useState(null);
  let r = (rows || []).filter((x) => (!q || searchKeys.some((k) => String(x[k] ?? "").toLowerCase().includes(q.toLowerCase()))) && filters.every((fl) => !f[fl.key] || String(x[fl.key]) === f[fl.key]));
  if (sort) r = [...r].sort((a, b) => { const va = sort.get ? sort.get(a) : a[sort.key]; const vb = sort.get ? sort.get(b) : b[sort.key]; return (va > vb ? 1 : va < vb ? -1 : 0) * sort.dir; });
  return (
    <div data-testid={testid}>
      <div className="flex flex-wrap gap-2 mb-3">
        <div className="relative flex-1 min-w-[200px]"><Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A8A29E]" /><input data-testid={`${testid}-search`} className="input pl-8" placeholder="Search…" value={q} onChange={(e) => setQ(e.target.value)} /></div>
        {filters.map((fl) => (
          <select key={fl.key} data-testid={`${testid}-filter-${fl.key}`} className="input w-auto" value={f[fl.key] || ""} onChange={(e) => setF({ ...f, [fl.key]: e.target.value })}>
            <option value="">All {fl.label}</option>{fl.options.map((o) => <option key={o}>{o}</option>)}
          </select>
        ))}
        <span className="self-center text-[12px] text-[#8A847C]">{r.length} records</span>
      </div>
      <div className="overflow-x-auto rounded-xl border border-[#E8E3D9] bg-white">
        <table className="tbl w-full">
          <thead><tr>{columns.map((c) => <th key={c.label} className="cursor-pointer select-none" onClick={() => setSort({ key: c.key, get: c.sortGet, dir: sort?.key === c.key ? -sort.dir : 1 })} data-testid={`${testid}-sort-${c.key || c.label}`}>{c.label}{sort?.key === c.key ? (sort.dir > 0 ? " ↑" : " ↓") : ""}</th>)}</tr></thead>
          <tbody>
            {r.map((x) => (
              <tr key={x.id} className={`row ${selectedId === x.id ? "!bg-[var(--chip)]" : ""}`} onClick={() => onRow?.(x)} data-testid={`row-${x.id}`}>
                {columns.map((c) => <td key={c.label} className="tnum">{c.render ? c.render(x) : x[c.key]}</td>)}
              </tr>
            ))}
          </tbody>
        </table>
        {r.length === 0 && <Empty label={empty || "No records match these filters."} />}
      </div>
    </div>
  );
}

export function OpenWorld({ to, label }) {
  const nav = useNavigate();
  return <button className="btn btn-ghost !py-1.5 !px-3 text-[12px]" onClick={() => nav(to)} data-testid={`open-${to.replace(/[/?=]/g, "-")}`}>{label}<ArrowUpRight size={13} /></button>;
}

export const Bar = ({ value, color = "var(--accent)" }) => <div className="h-1.5 rounded-full bg-[#EFECE4] overflow-hidden w-full"><div className="h-full rounded-full transition-[width] duration-500" style={{ width: `${Math.min(100, value)}%`, background: color }} /></div>;
export const Field = ({ label, children }) => <label className="block"><span className="text-[11px] font-semibold uppercase tracking-wider text-[#8A847C]">{label}</span><div className="mt-1">{children}</div></label>;
export const Stat = ({ label, value, accent }) => <div><div className="text-[11px] text-[#8A847C]">{label}</div><div className={`text-[17px] font-bold tnum ${accent ? "text-[var(--accent)]" : ""}`}>{value}</div></div>;
